import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import AnalysisForm from './components/AnalysisForm';
import ResultDisplay from './components/ResultDisplay';
import AdminPanel from './components/AdminPanel';
import FavoritesPanel from './components/FavoritesPanel';
import AnalysisProgress from './components/AnalysisProgress';
import { AnalysisResult } from './types';
import { buildApiUrl, API_ENDPOINTS } from './utils/api';

const MainPage: React.FC = () => {
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [targetProgress, setTargetProgress] = useState(0); // 目标进度（从服务器收到的）
  const [progressMessage, setProgressMessage] = useState('');
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 平滑进度动画效果
  useEffect(() => {
    if (loading) {
      // 清除之前的定时器
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }

      // 启动平滑进度动画
      progressIntervalRef.current = setInterval(() => {
        setProgress((prevProgress) => {
          // 如果当前进度小于目标进度，缓慢增加
          if (prevProgress < targetProgress) {
            // 计算增量：距离目标越远，增量越大，但最大不超过2%
            // 这样可以更快地追赶，确保用户能看到每个阶段
            const distance = targetProgress - prevProgress;
            const increment = Math.min(2, Math.max(0.3, distance / 5));
            return Math.min(targetProgress, prevProgress + increment);
          }
          // 如果已经达到目标进度，但目标进度小于100%，继续缓慢增加
          if (prevProgress >= targetProgress && targetProgress < 100) {
            // 非常缓慢地增加，给用户希望（每100ms增加0.1%，相当于每秒1%）
            // 但不超过99%，留1%等待最终完成
            return Math.min(99, prevProgress + 0.1);
          }
          return prevProgress;
        });
      }, 100); // 每100ms更新一次，让进度条平滑移动

      return () => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    } else {
      // 停止加载时清除定时器
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
  }, [loading, targetProgress]);

  const handleAnalyze = async (asset: string, variable: string) => {
    setLoading(true);
    setResult(null);
    setProgress(0); // 初始进度从0开始
    setTargetProgress(0); // 初始目标进度设为0，等待后端消息
    setProgressMessage('准备开始分析...');
    
    try {
      // 使用SSE接收进度更新（通过fetch读取流）
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ANALYZE_STREAM), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ asset, variable: variable || '' }),
      });

      if (!response.ok) {
        throw new Error('分析失败');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (!reader) {
        throw new Error('无法读取响应流');
      }

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          // 跳过空行
          if (!line.trim()) continue;
          
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6).trim();
              if (!jsonStr) continue; // 跳过空数据
              
              const data = JSON.parse(jsonStr);
              console.log('收到SSE数据:', data); // 调试日志
              
              if (data.type === 'progress') {
                // 更新目标进度和消息，平滑动画会自动追赶
                const progressValue = data.progress || 0;
                const messageValue = data.message || '';
                
                console.log(`进度更新: ${progressValue}% - ${messageValue}`); // 调试日志
                
                // 立即更新消息，确保用户能看到每个阶段
                setProgressMessage(messageValue);
                setTargetProgress(progressValue);
                
                // 立即更新进度到接近目标值，确保用户能看到每个阶段
                // 如果进度跳跃较大（超过10%），立即跳到目标值-2%，让动画平滑完成
                setProgress((prev) => {
                  const diff = progressValue - prev;
                  if (diff > 10) {
                    // 大跳跃：立即跳到目标值-2%，让动画平滑完成最后2%
                    return Math.max(prev, progressValue - 2);
                  } else if (diff > 5) {
                    // 中等跳跃：跳到目标值-3%
                    return Math.max(prev, progressValue - 3);
                  } else {
                    // 小跳跃：保持当前值，让动画平滑追赶
                    return prev;
                  }
                });
              } else if (data.type === 'result') {
                console.log('收到分析结果'); // 调试日志
                setTargetProgress(100);
                setProgress(100);
                setProgressMessage('分析完成！');
                setTimeout(() => {
                  setResult(data.data);
                  setLoading(false);
                }, 500);
                return;
              } else if (data.type === 'error') {
                console.error('收到错误:', data.message);
                throw new Error(data.message);
              }
            } catch (e) {
              console.error('解析SSE数据失败:', e, '原始数据:', line);
            }
          }
        }
      }
      
      // 如果循环结束但没有收到result，可能是连接中断
      console.warn('SSE流结束，但没有收到完成信号');
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
      setProgress(0);
      setTargetProgress(0);
      setProgressMessage('');
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      alert('分析失败，请检查后端服务是否运行');
    }
  };

  const handleAssetSelect = (asset: string) => {
    // 通过事件通知表单组件更新
    const event = new CustomEvent('assetSelected', { detail: asset });
    window.dispatchEvent(event);
  };

  const handleVariableSelect = (variable: string) => {
    // 通过事件通知表单组件更新
    const event = new CustomEvent('variableSelected', { detail: variable });
    window.dispatchEvent(event);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Trigger
          </h1>
          <div className="text-gray-600">
            <p>输入某事件或数据因子，帮你分析对标的的影响</p>
            <p>只输入标的，则自动帮你分析出影响价格的 3 个因子</p>
          </div>
        </header>

        <div className="flex gap-6 max-w-7xl mx-auto">
          {/* 左侧收藏标签池 */}
          <div className="flex-shrink-0">
            <FavoritesPanel 
              onAssetSelect={handleAssetSelect}
              onVariableSelect={handleVariableSelect}
            />
          </div>

          {/* 中间主要内容区域 */}
          <div className="flex-1 min-w-0">
            <AnalysisForm onAnalyze={handleAnalyze} loading={loading} />
            
            {/* 进度显示 */}
            {loading && (
              <div className="mt-8">
                <AnalysisProgress progress={progress} message={progressMessage} />
              </div>
            )}
            
            {/* 结果显示 */}
            {result && !loading && (
              <div className="mt-8">
                <ResultDisplay result={result} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// 导航组件 - 只在非管理页面显示
const Navigation: React.FC = () => {
  return (
    <nav className="bg-white shadow-sm mb-8">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="text-xl font-bold text-gray-800">
            事件因子驱动分析系统
          </Link>
        </div>
      </div>
    </nav>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route 
          path="/" 
          element={
            <>
              <Navigation />
              <MainPage />
            </>
          } 
        />
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
    </Router>
  );
};

export default App;


